import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { getBillTypeBadge } from '../components/BillTypeBadge';
import { useBreakpoint } from '../useBreakpoint';
import { BillStageDates, BillTimelineFields, MemberIndexEntry, Party } from '../types/types';
import { getBill, getCommitteeList, getMemberIndexDocOf, getMemberSummaries, getParties, getScope, knessetOn } from '../data/cache';
import { photoFor } from '../utils/names';
import { stageInfo } from '../utils/stages';
import { buttonReset, color, font, radius, shadow, space, text, tone as tones, weight } from '../theme';
import PageContainer from '../layout/PageContainer';
import TwoPane from '../layout/TwoPane';
import DetailHeader from '../components/DetailHeader';
import { Card, CardHeader, Section } from '../components/Card';
import { Badge } from '../components/Badge';
import { SegmentBar } from '../components/Bar';
import ListRow from '../components/ListRow';
import Avatar from '../components/Avatar';
import SearchField from '../components/SearchField';
import Button from '../components/Button';
import Icon from '../components/Icon';
import { EmptyState, ErrorState } from '../components/States';
import { Skeleton, SkeletonList, SkeletonText } from '../components/Skeleton';
import { AmendsFact, AmendsLine, BackgroundFiles, BillRefsCard, BillRefsSection, BillTimeline, DatedProgress, MergedIntoCard, mergeCaption } from '../components/BillTimeline';

/* ── Config ─────────────────────────────────────────────────────────────── */

const progressSteps = [
  { key: 'דיון מוקדם', label: 'דיון מוקדם' },
  { key: 'בוועדה', label: 'בוועדה' },
  { key: 'קריאה ראשונה', label: 'קריאה ראשונה' },
  { key: 'קריאה שנייה ושלישית', label: 'קריאה ב׳+ג׳' },
  { key: 'קריאה שלישית', label: 'קריאה שלישית' },
  { key: 'עברה', label: 'עברה' },
];
const stageToStep: Record<string, number> = {
  'דיון מוקדם': 0, 'בוועדה': 1, 'קריאה ראשונה': 2,
  'קריאה שנייה ושלישית': 3, 'קריאה שלישית': 4, 'עברה': 5,
};
// The date under each progress step (bills with a timeline)
const stepDateKey: Record<string, keyof BillStageDates> = {
  'דיון מוקדם': 'preliminary', 'בוועדה': 'committee', 'קריאה ראשונה': 'first',
  'קריאה שנייה ושלישית': 'second', 'קריאה שלישית': 'third', 'עברה': 'third',
};
const typeLabels: Record<string, string> = { private: 'פרטית', government: 'ממשלתית', committee: 'ועדה' };

// Vote colors (status, shared with the result badges)
const VOTE = {
  for: { label: 'בעד', bar: '#3f7a16', fg: tones.success.fg, bg: tones.success.bg },
  against: { label: 'נגד', bar: '#E24B4A', fg: tones.danger.fg, bg: tones.danger.bg },
  abstain: { label: 'נמנע', bar: '#c4ccd6', fg: color.textSecondary, bg: color.surfaceMuted },
};

interface BillDocument { id: string; groupType: string; fileType: string; filePath: string; lastUpdated: string }
interface CoInitiator { personId: number; name: string; docId: string; ordinal: number }
interface MkVote { mkDocId: string; result: 'for' | 'against' | 'abstain' }
interface PlenumVote {
  voteId: number;
  date: string;
  reading: string | null;
  totalFor: number;
  totalAgainst: number;
  totalAbstain: number;
  isAccepted: boolean;
  mkVotes: MkVote[];
}
// The timeline fields come from --bill-timeline: progressed bills only, so all optional
interface BillData extends BillTimelineFields {
  id: string;
  title?: string;
  status?: string;
  simpleStage?: string;
  billType?: string;
  isBasicLaw?: boolean;
  topics?: string[];
  proposerId?: string;
  committeeId?: string | null;
  ministryName?: string;
  plainSummary?: string;
  officialPdfUrl?: string;
  lastUpdated?: string;
  documents?: BillDocument[];
  coInitiators?: CoInitiator[];
  plenumVotes?: PlenumVote[];
  plenumVote?: PlenumVote;
}

const formatDate = (iso?: string) => (iso ? new Date(iso).toLocaleDateString('he-IL') : '');

/* ── Vote card ──────────────────────────────────────────────────────────── */

// `partyAtVote`: each voter's party in the vote's Knesset when that Knesset is archived (a
// re-elected MK's current party is a later Knesset's faction); else the current party counts
const VoteCard = ({ pv, isPrimary, members, parties, partyAtVote, from }: {
  pv: PlenumVote;
  isPrimary: boolean;
  members: Record<string, MemberIndexEntry>;
  parties: Record<string, Party>;
  partyAtVote?: Record<string, string | null>;
  from: string;
}) => {
  const [expanded, setExpanded] = useState(isPrimary);
  const [search, setSearch] = useState('');

  const mkVotes = pv.mkVotes || [];
  const hasMkVotes = mkVotes.length > 0;
  const voteDate = new Date(pv.date).toLocaleDateString('he-IL', { day: 'numeric', month: 'short', year: 'numeric' });

  // Coalition (true), opposition (false) or unknown (null) for a voter
  const isCoalition = (docId: string): boolean | null => {
    const partyId = partyAtVote && Object.prototype.hasOwnProperty.call(partyAtVote, docId) ? partyAtVote[docId] : members[docId]?.partyId;
    const p = partyId ? parties[partyId] : undefined;
    return p ? !!p.isCoalition : null;
  };
  const bloc = { coalition: { for: 0, against: 0, abstain: 0 }, opposition: { for: 0, against: 0, abstain: 0 } };
  for (const v of mkVotes) {
    const c = isCoalition(v.mkDocId);
    if (c === true) bloc.coalition[v.result]++;
    else if (c === false) bloc.opposition[v.result]++;
  }
  // A rebel voted against their bloc's majority (abstaining is not rebelling)
  const majority = (b: { for: number; against: number }) => (b.for >= b.against ? 'for' : 'against');
  const isRebel = (v: MkVote): boolean => {
    if (v.result === 'abstain') return false;
    const c = isCoalition(v.mkDocId);
    if (c === null) return false;
    return v.result !== majority(c ? bloc.coalition : bloc.opposition);
  };
  const rebelCount = mkVotes.filter(isRebel).length;

  const term = search.trim();
  const matches = (v: MkVote) => !term || (members[v.mkDocId]?.name || '').includes(term);
  const searched = term ? mkVotes.find(matches) : undefined;
  const hasBloc = bloc.coalition.for + bloc.coalition.against + bloc.coalition.abstain + bloc.opposition.for + bloc.opposition.against + bloc.opposition.abstain > 0;

  return (
    <Card padding={0} style={{ boxShadow: `${shadow.card}, inset 0 0 0 1px ${pv.isAccepted ? '#cfe3c2' : '#f2c9c9'}` }}>
      <button
        type="button"
        aria-expanded={hasMkVotes ? expanded : undefined}
        onClick={() => hasMkVotes && setExpanded(e => !e)}
        style={{ ...buttonReset, width: '100%', boxSizing: 'border-box', display: 'flex', alignItems: 'flex-start', gap: space.md, padding: space.lg, cursor: hasMkVotes ? 'pointer' : 'default' }}
      >
        <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: space.xs }}>
          <span style={{ fontSize: font.label, fontWeight: weight.bold }}>הצבעה במליאה</span>
          <span style={{ display: 'flex', alignItems: 'center', gap: space.sm, flexWrap: 'wrap', fontSize: font.small, color: color.textSecondary }}>
            {pv.reading && <Badge tone="warning">קריאה {pv.reading}</Badge>}
            {voteDate}
          </span>
        </span>
        <Badge tone={pv.isAccepted ? 'success' : 'danger'} style={{ fontSize: font.label, padding: '4px 10px' }}>{pv.isAccepted ? 'עברה' : 'לא עברה'}</Badge>
        {hasMkVotes && <span style={{ color: color.textTertiary, paddingTop: 4 }}><Icon name={expanded ? 'chevronUp' : 'chevronDown'} size={18} /></span>}
      </button>

      <div style={{ padding: `0 ${space.lg}px ${space.lg}px`, display: 'flex', flexDirection: 'column', gap: space.sm }}>
        <SegmentBar
          height={10}
          segments={[
            { key: 'for', value: pv.totalFor, color: VOTE.for.bar },
            { key: 'against', value: pv.totalAgainst, color: VOTE.against.bar },
            { key: 'abstain', value: pv.totalAbstain, color: VOTE.abstain.bar },
          ]}
          label={`בעד ${pv.totalFor}, נגד ${pv.totalAgainst}, נמנעים ${pv.totalAbstain}`}
        />
        <div style={{ display: 'flex', gap: space.md, fontSize: font.small, fontWeight: weight.bold }}>
          <span style={{ color: VOTE.for.fg }}>בעד {pv.totalFor}</span>
          <span style={{ color: VOTE.against.fg }}>נגד {pv.totalAgainst}</span>
          {pv.totalAbstain > 0 && <span style={{ color: VOTE.abstain.fg, fontWeight: weight.regular }}>נמנע {pv.totalAbstain}</span>}
        </div>

        {expanded && hasMkVotes && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: space.md, marginTop: space.sm }}>
            {hasBloc && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: space.sm }}>
                {([
                  { key: 'coalition', label: 'קואליציה', counts: bloc.coalition, bg: color.primarySoft, fg: color.primaryInk },
                  { key: 'opposition', label: 'אופוזיציה', counts: bloc.opposition, bg: '#FCEBEB', fg: '#501313' },
                ]).map(b => (
                  <div key={b.key} style={{ background: b.bg, borderRadius: radius.md, padding: `${space.sm}px ${space.md}px` }}>
                    <div style={{ fontSize: font.caption, fontWeight: weight.bold, color: b.fg, marginBottom: space.xxs }}>{b.label}</div>
                    <div style={{ fontSize: font.small, color: b.fg }}>
                      בעד {b.counts.for} · נגד {b.counts.against}{b.counts.abstain > 0 && ` · נמנע ${b.counts.abstain}`}
                    </div>
                  </div>
                ))}
              </div>
            )}
            {rebelCount > 0 && <span><Badge tone="warning">{rebelCount} ח״כים הצביעו נגד הגוש שלהם</Badge></span>}

            <SearchField variant="flat" value={search} onChange={setSearch} placeholder="חיפוש ח״כ" />

            {searched && members[searched.mkDocId] && (() => {
              const mk = members[searched.mkDocId];
              const c = isCoalition(searched.mkDocId);
              const v = VOTE[searched.result];
              return (
                <Link to={`/members/${searched.mkDocId}`} state={{ from }} style={{ display: 'flex', alignItems: 'center', gap: space.md, padding: `${space.sm + 2}px ${space.md}px`, borderRadius: radius.md, background: v.bg, color: color.text, textDecoration: 'none' }}>
                  <Avatar name={mk.name} src={photoFor(mk, 36)} size={36} />
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: 'block', fontSize: font.label, fontWeight: weight.medium }}>{mk.name}</span>
                    <span style={{ display: 'block', fontSize: font.caption, color: color.textSecondary }}>{c === true ? 'קואליציה' : c === false ? 'אופוזיציה' : ''}</span>
                  </span>
                  <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: space.xxs }}>
                    <span style={{ fontSize: font.label, fontWeight: weight.bold, color: v.fg }}>{v.label}</span>
                    {isRebel(searched) && <Badge>נגד הגוש</Badge>}
                  </span>
                </Link>
              );
            })()}

            {(['for', 'against', 'abstain'] as const).map(result => {
              const group = mkVotes.filter(v => v.result === result);
              const shown = group.filter(matches);
              if (group.length === 0 || shown.length === 0) return null;
              const v = VOTE[result];
              return (
                <div key={result}>
                  <div style={{ fontSize: font.small, fontWeight: weight.bold, color: v.fg, marginBottom: space.xs + 2 }}>
                    {v.label} ({group.length})
                    {term && shown.length < group.length && <span style={{ color: color.textSecondary, fontWeight: weight.regular }}> · מוצגים {shown.length}</span>}
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: space.xs }}>
                    {shown.map(vote => {
                      const mk = members[vote.mkDocId];
                      if (!mk) return null;
                      const rebel = isRebel(vote);
                      const lastName = mk.name.split(' ').pop() || mk.name;
                      return (
                        <Link
                          key={vote.mkDocId}
                          to={`/members/${vote.mkDocId}`}
                          state={{ from }}
                          title={rebel ? `${mk.name} · הצביע/ה נגד הגוש` : mk.name}
                          aria-label={rebel ? `${mk.name}, נגד הגוש` : mk.name}
                          style={{
                            display: 'flex', alignItems: 'center', gap: space.xs, minHeight: 32,
                            padding: `2px ${space.sm}px 2px ${space.xxs + 1}px`, borderRadius: radius.pill,
                            background: v.bg, color: v.fg, textDecoration: 'none', fontSize: font.caption,
                            fontWeight: rebel ? weight.bold : weight.medium,
                            boxShadow: rebel ? `inset 0 0 0 1.5px ${v.fg}` : 'none',
                          }}
                        >
                          <Avatar name={mk.name} src={photoFor(mk, 24)} size={24} />
                          {lastName}
                          {rebel && <Icon name="alert" size={14} strokeWidth={2.2} />}
                        </Link>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Card>
  );
};

/* ── Page ───────────────────────────────────────────────────────────────── */

const BillDetailPage = () => {
  const { billId } = useParams();
  const navigate = useNavigate();
  const { isDesktop } = useBreakpoint();

  const [bill, setBill] = useState<BillData | null>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'notFound' | 'error'>('loading');
  const [reloadKey, setReloadKey] = useState(0);
  const [members, setMembers] = useState<Record<string, MemberIndexEntry>>({});
  const [parties, setParties] = useState<Record<string, Party>>({});
  // Knesset → voter → party then, for votes held in an archived Knesset
  const [partiesThen, setPartiesThen] = useState<Record<number, Record<string, string | null>>>({});
  const [voteKnesset, setVoteKnesset] = useState<Record<string, number>>({});
  const [committeeName, setCommitteeName] = useState('');
  // A merged bill's leading bill: its stage, the bills merged alongside, the merge approval
  const [leader, setLeader] = useState<BillData | null>(null);

  // The bill doc first; voters, co-initiators, parties and the committee name fill in after
  useEffect(() => {
    if (!billId) return;
    let cancelled = false;
    setStatus('loading');
    getBill(billId).then(raw => {
      if (cancelled) return;
      if (!raw) { setStatus('notFound'); return; }
      const b = raw as unknown as BillData;
      setBill(b);
      setStatus('ready');

      const votes: PlenumVote[] = b.plenumVotes || (b.plenumVote ? [b.plenumVote] : []);
      const ids: string[] = [];
      if (b.proposerId) ids.push(b.proposerId);
      for (const ci of b.coInitiators || []) if (ci.docId) ids.push(ci.docId);
      for (const pv of votes) for (const v of pv.mkVotes || []) ids.push(v.mkDocId);
      Promise.all([getMemberSummaries(ids), getParties()]).then(([m, ps]) => {
        if (cancelled) return;
        setMembers(m);
        const byId: Record<string, Party> = {};
        for (const p of ps) byId[p.id] = p;
        setParties(byId);
      }).catch(() => { /* ok — names fill in when available */ });

      // Votes of an archived Knesset are split by the parties of that Knesset
      getScope().then(scope => {
        const byVote: Record<string, number> = {};
        const archived: number[] = [];
        for (const pv of votes) {
          const k = knessetOn(scope, pv.date);
          byVote[String(pv.voteId)] = k;
          if (k !== scope.knesset && scope.archivedKnessets.indexOf(k) !== -1 && archived.indexOf(k) === -1) archived.push(k);
        }
        if (cancelled) return;
        setVoteKnesset(byVote);
        return Promise.all(archived.map(k => getMemberIndexDocOf(k).then(d => {
          const map: Record<string, string | null> = {};
          if (d) for (const id of Object.keys(d.members)) map[id] = d.members[id].partyId || null;
          return [k, map] as [number, Record<string, string | null>];
        }))).then(pairs => {
          if (cancelled) return;
          const then: Record<number, Record<string, string | null>> = {};
          for (const [k, map] of pairs) then[k] = map;
          setPartiesThen(then);
        });
      }).catch(() => { /* ok — falls back to the current parties */ });

      if (b.committeeId) {
        getCommitteeList().then(list => {
          const c = list.find(cc => cc.id === b.committeeId);
          if (!cancelled && c) setCommitteeName(c.name);
        }).catch(() => { /* ok */ });
      }

      const leaderId = b.mergedInto && b.mergedInto.billDocId;
      if (leaderId) {
        getBill(leaderId).then(raw => {
          if (!cancelled && raw) setLeader(raw as unknown as BillData);
        }).catch(() => { /* ok — the card shows the name from this bill's doc */ });
      }
    }).catch(() => { if (!cancelled) setStatus('error'); });
    return () => { cancelled = true; };
  }, [billId, reloadKey]);

  const plenumVotes = useMemo<PlenumVote[]>(() => (bill ? (bill.plenumVotes || (bill.plenumVote ? [bill.plenumVote] : [])) : []), [bill]);

  if (status === 'loading') {
    return (
      <Layout>
        <div data-skeleton="true" style={{ padding: space.lg, display: 'flex', flexDirection: 'column', gap: space.lg }}>
          <Card><div style={{ display: 'flex', flexDirection: 'column', gap: space.md }}><Skeleton width={140} height={20} /><SkeletonText lines={2} lineHeight={18} /></div></Card>
          <SkeletonList rows={3} />
        </div>
      </Layout>
    );
  }
  if (status === 'error') {
    return <Layout><div style={{ padding: space.lg }}><Card padding={0}><ErrorState onRetry={() => setReloadKey(k => k + 1)} /></Card></div></Layout>;
  }
  if (status === 'notFound' || !bill) {
    return (
      <Layout>
        <div style={{ padding: space.lg }}>
          <Card padding={0}>
            <EmptyState icon="document" title="הצעת החוק לא נמצאה" body="ייתכן שהקישור שגוי." action={<Button onClick={() => navigate('/legislation', { replace: true })}>לכל הצעות החוק</Button>} />
          </Card>
        </div>
      </Layout>
    );
  }

  const simpleStage = bill.simpleStage || 'אחר';
  const currentStep = stageToStep[simpleStage] ?? -1;
  const isStopped = ['נעצרה', 'מוזגה', 'הוסבה'].includes(simpleStage);
  const from = bill.title || 'הצעת חוק';
  const documents = bill.documents || [];
  const coInitiators = bill.coInitiators || [];
  const proposer = bill.proposerId ? members[bill.proposerId] : undefined;
  const typeBadge = getBillTypeBadge(bill.billType || 'private');
  // From the bill timeline (--bill-timeline); absent on preliminary bills and ones not fetched yet
  const timeline = bill.timeline || [];
  const amends = bill.amends || [];
  const mergedInto = bill.mergedInto && bill.mergedInto.name ? bill.mergedInto : null;
  const mergedBills = bill.mergedBills || [];
  const mergedAlongside = leader && mergedInto ? (leader.mergedBills || []).filter(r => r.billDocId !== bill.id) : [];
  const splitFrom = bill.splitFrom && bill.splitFrom.name ? bill.splitFrom : null;
  const splitBills = bill.splitBills || [];
  const background = bill.background || [];

  const headerContent = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: space.sm }}>
      <span style={{ display: 'flex', gap: space.xs + 2, flexWrap: 'wrap' }}>
        <Badge tone={stageInfo(simpleStage).tone}>{bill.status || simpleStage}</Badge>
        {bill.isBasicLaw && <Badge tone="special">חוק יסוד</Badge>}
        <Badge tone={bill.billType === 'government' ? 'info' : 'neutral'}>{typeBadge.label}</Badge>
      </span>
      <h1 style={{ ...text.entityTitle, fontSize: 21, lineHeight: 1.4 }}>{bill.title}</h1>
      {amends.length > 0 && <AmendsLine amends={amends} />}
      {bill.topics && bill.topics.length > 0 && <div style={{ fontSize: font.small, color: color.textSecondary }}>{bill.topics.join(' · ')}</div>}
    </div>
  );

  // Merge and split links (desktop: in the side panel; phone/tablet: under the timeline)
  const refCards = (mergedBills.length > 0 || splitFrom || splitBills.length > 0) ? (
    <>
      {mergedBills.length > 0 && <BillRefsCard title="הצעות שמוזגו לתוכה" refs={mergedBills} badge="מוזגה" from={from} />}
      {splitFrom && <BillRefsCard title="פוצלה מתוך" refs={[splitFrom]} showCount={false} from={from} />}
      {splitBills.length > 0 && <BillRefsCard title="הצעות שפוצלו ממנה" refs={splitBills} from={from} />}
    </>
  ) : null;
  const alongsideSection = mergedAlongside.length > 0 ? <BillRefsSection title="מוזגו איתה" refs={mergedAlongside} from={from} /> : null;

  // Co-initiators (desktop: in the side panel, wrapping instead of scrolling sideways)
  const coInitiatorsCard = coInitiators.length > 1 ? (
    <Card>
      <CardHeader title="יוזמי הצעת החוק" meta={coInitiators.length} />
      <div style={{ display: 'flex', gap: space.sm, ...(isDesktop ? { flexWrap: 'wrap' as const } : { overflowX: 'auto' as const, scrollbarWidth: 'none' as const }) }}>
        {coInitiators.map((p, i) => {
          const mk = p.docId ? members[p.docId] : undefined;
          const item = (
            <>
              <Avatar name={p.name} src={mk ? photoFor(mk, 48) : undefined} size={48} />
              <span style={{ fontSize: font.caption, fontWeight: i === 0 ? weight.bold : weight.regular, color: i === 0 ? color.primary : color.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 64 }}>{p.name.split(' ').pop()}</span>
              {i === 0 && <span style={{ fontSize: font.caption, color: color.textSecondary }}>יוזם/ת</span>}
            </>
          );
          const itemStyle: React.CSSProperties = { flex: '0 0 auto', width: 64, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: space.xxs, color: color.text, textDecoration: 'none' };
          // Only co-initiators mapped to a member doc have a page to link to
          return p.docId
            ? <Link key={p.personId} to={`/members/${p.docId}`} state={{ from }} title={p.name} style={itemStyle}>{item}</Link>
            : <span key={p.personId} title={p.name} style={itemStyle}>{item}</span>;
        })}
      </div>
    </Card>
  ) : null;

  // Facts (desktop: in the side panel)
  const proposerLabel = proposer?.name || bill.ministryName || (bill.billType === 'government' ? 'הממשלה' : 'לא ידוע');
  const factRow = (label: string, value: React.ReactNode, i: number) => (
    <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: space.md, minHeight: 48, padding: `${space.xs}px ${space.lg}px`, borderTop: i > 0 ? `1px solid ${color.divider}` : 'none', fontSize: font.label }}>
      <span style={{ color: color.textSecondary, flexShrink: 0 }}>{label}</span>
      <span style={{ fontWeight: weight.medium, textAlign: 'left' }}>{value}</span>
    </div>
  );
  const linkStyle: React.CSSProperties = { color: color.primary, textDecoration: 'none' };
  const facts: { label: string; value: React.ReactNode }[] = [
    { label: 'מגיש/ה', value: proposer ? <Link to={`/members/${bill.proposerId}`} state={{ from }} style={linkStyle}>{proposerLabel}</Link> : proposerLabel },
    { label: 'סוג', value: typeLabels[bill.billType || ''] || bill.billType || '' },
  ];
  if (bill.committeeId && committeeName) facts.push({ label: 'ועדה מטפלת', value: <Link to={`/committees/${bill.committeeId}`} state={{ from }} style={linkStyle}>{committeeName}</Link> });
  // Desktop only: phone and tablet have it under the title
  if (isDesktop && amends.length > 0) facts.push({ label: 'מתקן את', value: <AmendsFact amends={amends} /> });
  if (coInitiators.length > 1) facts.push({ label: 'שותפים', value: `${coInitiators.length} ח״כים` });
  if (bill.lastUpdated) facts.push({ label: 'עדכון אחרון', value: formatDate(bill.lastUpdated) });
  const infoRows = <Card padding={0}>{facts.map((f, i) => factRow(f.label, f.value, i))}</Card>;

  return (
    <Layout width="full" breadcrumbs={[{ label: bill.title && bill.title.length > 60 ? `${bill.title.substring(0, 60)}…` : (bill.title || 'הצעת חוק') }]}>
      <PageContainer>
        {/* Header: on desktop a card across both columns (no back link); on phone and tablet the band */}
        {isDesktop
          ? <Card style={{ marginBottom: space.lg }}>{headerContent}</Card>
          : <DetailHeader fallback="/legislation" fallbackLabel="חקיקה">{headerContent}</DetailHeader>}

        <div style={isDesktop ? undefined : { padding: space.lg }}>
          {/* Desktop: the facts and co-initiators move to a side panel; phone/tablet keep them inline.
              Not sticky: with dozens of co-initiators the panel is taller than the screen. */}
          <TwoPane side={<div style={{ display: 'flex', flexDirection: 'column', gap: space.md }}>{infoRows}{coInitiatorsCard}{alongsideSection}{refCards}</div>} sideWidth={300} sideWhenStacked="hidden" stickySide={false}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: space.lg }}>
              {bill.plainSummary && (
                <Card style={{ background: color.primarySoft, boxShadow: 'none' }}>
                  <div style={{ fontSize: font.small, fontWeight: weight.bold, color: color.primaryInk, marginBottom: space.xs }}>סיכום בשפה פשוטה</div>
                  <p style={{ margin: 0, fontSize: font.label, lineHeight: 1.65, color: color.primaryInk }}>{bill.plainSummary}</p>
                </Card>
              )}

              {/* With a timeline, every step is labelled and dated (2A); otherwise as before */}
              {!isStopped && currentStep >= 0 && bill.stageDates && (
                <Card>
                  <CardHeader title="שלב בחקיקה" meta={`${currentStep + 1} מתוך ${progressSteps.length}`} />
                  <DatedProgress current={currentStep} steps={progressSteps.map(s => ({ ...s, date: bill.stageDates && bill.stageDates[stepDateKey[s.key]] }))} />
                </Card>
              )}
              {!isStopped && currentStep >= 0 && !bill.stageDates && (
                <Card>
                  <CardHeader title="שלב בחקיקה" meta={`${currentStep + 1} מתוך ${progressSteps.length}`} />
                  <div style={{ display: 'flex', alignItems: 'center', gap: space.xs }} aria-hidden="true">
                    {progressSteps.map((step, i) => (
                      <React.Fragment key={step.key}>
                        <span style={{ width: 12, height: 12, borderRadius: '50%', flexShrink: 0, background: i <= currentStep ? color.primary : color.track, boxShadow: i === currentStep ? `0 0 0 4px ${color.primarySoft}` : 'none' }} />
                        {i < progressSteps.length - 1 && <span style={{ flex: 1, height: 2, background: i < currentStep ? color.primary : color.track }} />}
                      </React.Fragment>
                    ))}
                  </div>
                  <div style={{ fontSize: font.label, fontWeight: weight.medium, color: color.primary, marginTop: space.sm }}>{progressSteps[currentStep]?.label || bill.status}</div>
                </Card>
              )}

              {/* Merged: a link to the leading bill (2 · מצב) instead of the red card */}
              {mergedInto && (
                <MergedIntoCard
                  leader={mergedInto}
                  leaderStage={leader ? leader.simpleStage : undefined}
                  caption={mergeCaption(timeline, leader ? leader.timeline : undefined, committeeName)}
                  from={from}
                />
              )}
              {isStopped && !mergedInto && (
                <Card style={{ background: '#FCEBEB', boxShadow: 'none', textAlign: 'center' }}>
                  <span style={{ fontSize: font.label, fontWeight: weight.medium, color: '#501313' }}>
                    {simpleStage === 'נעצרה' ? 'הצעת החוק נעצרה' : simpleStage === 'מוזגה' ? 'הצעת החוק מוזגה עם הצעה אחרת' : 'הצעת החוק הוסבה להצעה לסדר היום'}
                  </span>
                </Card>
              )}
              {!isDesktop && alongsideSection}

              {timeline.length > 0 && (
                <BillTimeline
                  billId={bill.id}
                  billTitle={from}
                  timeline={timeline}
                  timelineTotal={bill.timelineTotal}
                  passed={simpleStage === 'עברה'}
                  meta={mergedInto ? 'עד המיזוג' : undefined}
                />
              )}
              {!isDesktop && refCards}

              {plenumVotes.length > 0 && plenumVotes.slice().reverse().map((pv, i) => (
                <VoteCard key={pv.voteId || i} pv={pv} isPrimary={i === 0} members={members} parties={parties} partyAtVote={partiesThen[voteKnesset[String(pv.voteId)]]} from={from} />
              ))}

              {/* Co-initiators + facts (inline on phone/tablet; desktop shows them in the side panel) */}
              {!isDesktop && coInitiatorsCard}
              {!isDesktop && infoRows}

              {documents.length > 0
                ? (
                  <Section title="מסמכים רשמיים" meta={documents.length}>
                    <Card padding={0}>
                      {documents.map((d, i) => (
                        <ListRow
                          key={`${d.id || ''}-${i}`}
                          divider={i > 0}
                          href={d.filePath}
                          leading={<span style={{ color: color.primary }}><Icon name="document" size={22} /></span>}
                          title={d.groupType || 'מסמך'}
                          subtitle={[d.fileType, formatDate(d.lastUpdated)].filter(Boolean).join(' · ')}
                        />
                      ))}
                    </Card>
                  </Section>
                )
                : bill.officialPdfUrl
                  ? <Card padding={0}><ListRow divider={false} href={bill.officialPdfUrl} leading={<span style={{ color: color.primary }}><Icon name="document" size={22} /></span>} title="הנוסח המלא" /></Card>
                  : <Card><p style={{ margin: 0, fontSize: font.small, color: color.textSecondary }}>קישור לנוסח המלא יתווסף בקרוב.</p></Card>}

              {background.length > 0 && <BackgroundFiles files={background} />}
            </div>
          </TwoPane>
        </div>
      </PageContainer>
    </Layout>
  );
};

export default BillDetailPage;
